// @flow
import {
  RESORT_ACCOMMODATION_TYPES,
  RESORT_ACCOMMODATION_EXCLUSIVE_COLLECTION,
} from 'Client/cms/toolkit/constants';
import { addTranslationKeys } from 'Src/i18n';
import { buildProductArgs } from 'Utils/graphql';
import graphQLClient from 'Utils/graphql-client/graphqlClient';
import { LINE_PRODUCT_ACCOMMODATIONS } from 'Utils/request/requestDictionary';

export type CMSProps = {
  bathroomLabel: string,
  capacityOfPeopleLabel: string,
  equipmentLabel: string,
  id?: string,
  isLineExclusiveCollection?: boolean,
  numberOfPeopleLabel: string,
  resortId: string,
  roomConfiguration: { singleRoom: string, connectingRooms: string, separatedRooms: string },
  seeMore: string,
  servicesLabel: string,
};

type GQLProps = {
  accommodationCategory?: { rankedCategories?: Array<{ id?: string, ranking?: string }> },
  config?: {
    common?: {
      backNavigation?: string,
      viewMore?: string,
      loadLess?: string,
      next?: string,
      previous?: string,
      scrollBackward?: string,
      scrollForward?: string,
    },
  },
  resort: {
    accommodationsIntroduction?: {
      description?: string,
      shortDescription?: string,
    },
    accommodations?: Array<Accommodation>,
    comfort?: number,
    type?: string,
  },
};

export type LineProps = CMSProps & GQLProps;

const getQuery = productArgs => {
  return `query {
    config {
      common {
        backNavigation
        viewMore
        loadLess
        next
        previous
        scrollBackward
        scrollForward
      }
    }
    accommodationCategory(${productArgs}) {
      rankedCategories: accommodations {
        id
        ranking
      }
    }
    resort(${productArgs}) {
      accommodationsIntroduction {
        description
        shortDescription
      }
      accommodations {
        bathroomEquipments {
          id
          label
        }
        category
        categoryId
        comfortId
        comfortType
        description
        equipments {
          id
          label
        }
        exclusiveCollection
        id
        label
        maxCapacity
        media {
          photos {
            url
          }
        }
        ranking
        roomType
        services {
          id
          label
        }
        outsideFacilities {
          id
          label
        }
        strengths {
          id
          label
        }
        surface
        view {
          id
          label
        }
      }
      comfort
      type
    }
  }`;
};

const commonAccommodationsResolver = ({
  isLineExclusiveCollection,
}: {
  isLineExclusiveCollection: boolean,
}) => (
  appContext: AppContext,
  props: *,
  pageContext: PageContext,
  location: ProductRouterLocation,
) => {
  const { urlParams } = location;
  const { resortId: productId } = props;
  const productArgs = buildProductArgs(productId, urlParams);

  const query = getQuery(productArgs);

  return graphQLClient
    .runQuery(query, appContext, { requestName: LINE_PRODUCT_ACCOMMODATIONS, strictErrors: false })
    .then(data => {
      addTranslationKeys(data.config);
      return { ...data, isLineExclusiveCollection };
    });
};

export const productAccommodationsResolver = {
  name: RESORT_ACCOMMODATION_TYPES,
  resolve: commonAccommodationsResolver({ isLineExclusiveCollection: false }),
};

export const productAccommodationsExclusiveCollectionResolver = {
  name: RESORT_ACCOMMODATION_EXCLUSIVE_COLLECTION,
  resolve: commonAccommodationsResolver({ isLineExclusiveCollection: true }),
};
